import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { TextField, Grid, Button, Divider, Card, FormControl, Typography, useMediaQuery, Box } from '@material-ui/core';
import clsx from 'clsx';
import _ from 'lodash';
import { DashboardHelpers, RegEx } from 'utils';
import { Roles } from 'utils/authHelpers';
import { useSettings, useUsers } from 'hooks';
import { FormControlLabel, FormGroup, Switch, FormLabel, Radio, RadioGroup } from '@mui/material';
import { ConfigContext } from 'context/ConfigContext';
import palette from 'theme/palette';
import { UserOrgEdit } from '.';
import { DropdownCheckbox } from 'components';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },

  formGroup: {
    width: '100%',
  },
  warning: {
    color: palette.warning.main,
  },
  sucess: {
    color: palette.success.main,
  },
  resendInviteMessageBox: { display: 'flex', justifyContent: 'left', height: '100%', alignItems: 'center' },
}));

const permissions = [
  { display_name: 'Organization: Write', scope: 'organization:write' },
  { display_name: 'Organization: Read', scope: 'organization:read' },
  { display_name: 'Property: Write', scope: 'property:write' },
  { display_name: 'Property: Read', scope: 'property:read' },
  { display_name: 'Device: Write', scope: 'device:write' },
  { display_name: 'Device: Read', scope: 'device:read' },
  { display_name: 'Field: Write', scope: 'field:write' },
  { display_name: 'Field: Read', scope: 'field:read' },
  { display_name: 'User: Write', scope: 'user:write' },
  { display_name: 'User: Read', scope: 'user:read' },
  { display_name: 'Event: Write', scope: 'event:write' },
  { display_name: 'Event: Read', scope: 'event:read' },
  { display_name: 'Health Intervals: Read', scope: 'crop:read' },
  { display_name: 'Health Intervals: Write', scope: 'crop:write' },
  { display_name: 'Organization: Cross', scope: 'organization:cross' },
  { display_name: 'Archive', scope: 'archive' },
];

const userDefaultPermissions = [
  'organization:read',
  'property:read',
  'property:write',
  'device:read',
  'device:write',
  'field:read',
  'field:write',
  'user:read',
  'event:read',
  'event:write',
  'crop:read',
  'openid',
  'profile',
  'archive',
  'offline_access',
];

const adminDefaultPermissions = [
  'organization:read',
  'organization:write',
  'property:read',
  'property:write',
  'device:read',
  'device:write',
  'field:read',
  'field:write',
  'user:read',
  'user:write',
  'event:read',
  'event:write',
  'crop:read',
  'crop:write',
  'openid',
  'profile',
  'archive',
  'offline_access',
];

const UserForm = ({ userToEdit, onSave, handleCloseModal, organizations, selectedOrganization, currentUserRoles }) => {
  const emptyUserForm = {
    login: '',
    password: '',
    password_confirm: '',
    display_name: '',
    organization: _.isEmpty(selectedOrganization) ? undefined : selectedOrganization,
  };

  const prefillUserForm = {
    password: '',
    password_confirm: '',
    display_name: userToEdit?.display_name ? userToEdit.display_name : '',
    organization: _.find(organizations, { id: userToEdit?.organization_id }),
  };

  const { t } = useTranslation();
  const classes = useStyles();

  const [formData, setFormData] = useState(userToEdit ? { ...prefillUserForm } : { ...emptyUserForm });
  const [formErrors, setFormErrors] = useState({
    login: '',
    password: '',
    password_confirm: '',
    display_name: '',
    organization: '',
  });
  const [formControl, setFormControl] = useState('form');
  const [currentUser, setCurrentUser] = useState();
  const matchesSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { isSuper, isAdmin } = useSettings();
  const highestRole = userToEdit ? DashboardHelpers.getHighestRole(userToEdit.roles) : Roles.User;
  const [selectedRoles, setSelectedRoles] = useState(userToEdit ? userToEdit.roles : [Roles.User]);
  const [selectedRadioRole, setSelectedRadioRole] = useState(highestRole);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [resendUserInviteMessage, setResendUserInviteMessage] = useState('');
  const [colorForResendInviteMessage, setColorForResendInviteMessage] = useState('');

  const [selectedPermissions, setSelectedPermissions] = useState(
    userToEdit ? userToEdit.permissions : [...userDefaultPermissions]
  );

  const { client_id } = useContext(ConfigContext);
  const { userInviteRequest } = useUsers();
  const formRoles = [
    { display_name: 'User', scope: Roles.User },
    { display_name: 'Admin', scope: Roles.Admin },
  ];

  if (isSuper) {
    formRoles.push({ display_name: 'Super', scope: Roles.Super });
  }

  const roleRadioControls = [];

  _.forEach(formRoles, (role) => {
    roleRadioControls.push(
      <FormControlLabel key={role.scope} value={role.scope} control={<Radio />} label={role.display_name} />
    );
  });

  const handleRoleRadio = (event, value) => {
    setSelectedRadioRole(value);
    setSelectedRoles([value]);
    switch (value) {
      case Roles.User:
        setSelectedPermissions(userDefaultPermissions);
        break;
      case Roles.Admin:
        setSelectedPermissions(adminDefaultPermissions);
        break;
      case Roles.Super:
        setSelectedPermissions(_.map(permissions, 'scope'));
        break;
      default:
        break;
    }
  };

  const handleFormDataChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const formDataValid = () => {
    let valid = true;
    let formErrorBuilder = formErrors;

    if (userToEdit) {
      // check for display name existing if updating user
      formErrorBuilder = DashboardHelpers.validateFormField(
        formData,
        'display_name',
        formErrorBuilder,
        t('form_field_needs') + t('display_name')
      );
    } else {
      // check for empty strings if creating user
      const formDataKeys = Object.keys(formData);
      _.forEach(formDataKeys, (formDataKey) => {
        formErrorBuilder = DashboardHelpers.validateFormField(
          formData,
          formDataKey,
          formErrorBuilder,
          t('form_field_needs') + t(formDataKey)
        );
      });
    }

    // check for email pattern in login
    if (!userToEdit && !RegEx.isEmail(formData.login)) {
      formErrorBuilder = { ...formErrorBuilder, login: t('invalid_email') };
      valid = false;
    }

    // check for mismatched passwords
    if (formErrorBuilder.password === '') {
      if (formData.password === formData.password_confirm) {
        formErrorBuilder = { ...formErrorBuilder, password_confirm: '' };
      } else {
        formErrorBuilder = { ...formErrorBuilder, password_confirm: t('mismatched_passwords') };
        valid = false;
      }
    }

    setFormErrors({ ...formErrorBuilder });

    valid =
      _.filter(formErrorBuilder, (val) => {
        return val !== '';
      }).length > 0
        ? false
        : true;
    return valid;
  };

  const buildUser = () => {
    let selPerms = [];
    let selRoles = [];

    if (selectedPermissions.length > 0) {
      selPerms = selPerms.concat(selectedPermissions);
      if (!selPerms.includes('openid')) {
        selPerms.push('openid');
      }
      if (!selPerms.includes('profile')) {
        selPerms.push('profile');
      }
    }

    if (selectedRoles.length > 0) {
      selRoles = selRoles.concat(selectedRoles);
    }

    if (!showAdvanced && selRoles.includes(Roles.Super)) {
      selRoles.push(Roles.Admin);
    }
    if (!showAdvanced && selRoles.includes(Roles.Admin)) {
      selRoles.push(Roles.User);
    }

    const finalPermsAdd = [];
    const finalRolesAdd = [];
    const finalPermsRemove = [];
    const finalRolesRemove = [];

    if (userToEdit) {
      _.forEach(userToEdit.permissions, (perm) => {
        if (!selPerms.includes(perm)) {
          finalPermsRemove.push({ permission: perm });
        }
      });

      _.forEach(userToEdit.roles, (role) => {
        if (!selRoles.includes(role)) {
          finalRolesRemove.push({ role: role });
        }
      });

      _.forEach(selPerms, (perm) => {
        if (!finalPermsRemove.includes(perm) && !userToEdit.permissions?.includes(perm)) {
          finalPermsAdd.push({ permission: perm });
        }
      });
      _.forEach(selRoles, (role) => {
        if (!finalRolesRemove.includes(role) && !userToEdit.roles?.includes(role)) {
          finalRolesAdd.push({ role: role });
        }
      });
    } else {
      _.forEach(selPerms, (perm) => {
        if (!finalPermsRemove.includes(perm)) {
          finalPermsAdd.push({ permission: perm });
        }
      });
      _.forEach(selRoles, (role) => {
        if (!finalRolesRemove.includes(role)) {
          finalRolesAdd.push({ role: role });
        }
      });
    }

    const user = {
      password: formData?.password,
      profile: { name: formData?.display_name.trim() },
      display_name: formData?.display_name.trim(),
      organization_id: formData?.organization?.id,
      permissions_update: { add: finalPermsAdd, remove: finalPermsRemove },
      roles_update: { add: finalRolesAdd, remove: finalRolesRemove },
    };

    if (userToEdit) {
      user.user_id = userToEdit.id;
    } else {
      user.login = formData?.login ? formData?.login.trim() : formData?.email;
    }

    return user;
  };

  const nextStep = (user) => {
    setCurrentUser(user);
    setFormControl('confirm');
  };

  const submitUser = () => {
    onSave(currentUser);
    handleCloseModal();
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (formDataValid()) {
      const user = buildUser();
      nextStep(user);
    }
  };

  const resendInvite = () => {
    const email = userToEdit?.profile?.email;
    if (email) {
      const user = {
        email: email,
        organization_id: userToEdit?.organization?.id,
        password: '-------',
        permissions_update: DashboardHelpers.DefaultUserPermissions,
        roles_update: { add: [{ role: Roles.User }] },
      };

      userInviteRequest(email, client_id, user).then((response) => {
        if (response && !response.error) {
          setResendUserInviteMessage(t('resend_invite_success'));
          setColorForResendInviteMessage('green');
        } else {
          setResendUserInviteMessage(t('resend_invite_failure'));
          setColorForResendInviteMessage('red');
        }
      });
    }
  };

  const lostFocus = (event, field) => {
    const realTimeValue = event.target.value;
    let formErrorBuilder = formErrors;

    const realTimeFormData = { [field]: realTimeValue };

    // check if empty
    formErrorBuilder = DashboardHelpers.validateFormField(
      realTimeFormData,
      field,
      formErrorBuilder,
      t('form_field_needs') + t(field)
    );

    // check for email pattern in login
    if (realTimeFormData.login) {
      if (!userToEdit && !RegEx.isEmail(realTimeFormData.login)) {
        formErrorBuilder = { ...formErrorBuilder, login: t('invalid_email') };
      }
    }

    // check for mismatched passwords
    if (realTimeFormData.password || realTimeFormData.password_confirm)
      if (formErrorBuilder.password === '') {
        if (realTimeFormData.password === formData.password_confirm) {
          formErrorBuilder = { ...formErrorBuilder, password_confirm: '' };
        } else if (realTimeFormData.password_confirm === formData.password) {
          formErrorBuilder = { ...formErrorBuilder, password_confirm: '' };
        } else {
          formErrorBuilder = { ...formErrorBuilder, password_confirm: t('mismatched_passwords') };
        }
      }

    setFormErrors({ ...formErrorBuilder });
  };

  return (
    <Card className={clsx(classes.root)}>
      {formControl === 'form' ? (
        <Box>
          <Typography component="h2" gutterBottom variant={matchesSmall ? 'h3' : 'h2'}>
            {userToEdit ? t('editing_user') + userToEdit.login : t('user_new')}
          </Typography>
          <form noValidate onSubmit={handleSave}>
            <Grid container spacing={2} direction="row">
              {!userToEdit && (
                <Grid item xs={12} sm={6}>
                  <Box>
                    <TextField
                      onBlur={(event) => {
                        lostFocus(event, 'login');
                      }}
                      placeholder={t('email')}
                      label={t('email')}
                      name="login"
                      variant="outlined"
                      fullWidth
                      required
                      value={formData?.login}
                      onChange={handleFormDataChange}
                      error={formErrors.login !== ''}
                      helperText={formErrors.login ? formErrors.login : t('email_example')}
                      autoComplete="off"
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Box>
                  <TextField
                    onBlur={(event) => {
                      lostFocus(event, 'display_name');
                    }}
                    placeholder={t('enter_name')}
                    label={t('full_name')}
                    name="display_name"
                    variant="outlined"
                    fullWidth
                    required={!userToEdit}
                    value={formData?.display_name}
                    onChange={handleFormDataChange}
                    error={formErrors.display_name !== ''}
                    helperText={formErrors.display_name}
                    autoComplete="off"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.passwordField}>
                  <TextField
                    onBlur={(event) => {
                      lostFocus(event, 'password');
                    }}
                    type="password"
                    placeholder={t('enter_password')}
                    label={t('password')}
                    name="password"
                    variant="outlined"
                    fullWidth
                    required={!userToEdit}
                    value={formData?.password}
                    onChange={handleFormDataChange}
                    error={formErrors.password !== ''}
                    helperText={formErrors.password}
                    autoComplete="off"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.passwordField}>
                  <TextField
                    onBlur={(event) => {
                      lostFocus(event, 'password_confirm');
                    }}
                    type="password"
                    placeholder={t('password_confirm')}
                    label={t('password_confirm')}
                    name="password_confirm"
                    variant="outlined"
                    disabled={formData.password === ''}
                    fullWidth
                    required={!userToEdit}
                    value={formData?.password_confirm}
                    onChange={handleFormDataChange}
                    error={formErrors.password_confirm !== ''}
                    helperText={formErrors.password_confirm}
                    autoComplete="off"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {isSuper && !userToEdit && (
                <Grid item xs={12} sm={6}>
                  <Box className={classes.paddingBottom}>
                    <UserOrgEdit
                      desiredOrg={formData.organization}
                      setUserOrg={(organization) => {
                        setFormData({ ...formData, organization: organization });
                      }}
                      organizationError={formErrors.organization !== ''}
                      organizationErrorMessage={formErrors.organization}
                    />
                  </Box>
                </Grid>
              )}

              {(isAdmin || isSuper) && (
                <>
                  <Grid item sm={6}>
                    <FormGroup>
                      <FormControlLabel
                        control={<Switch selected={showAdvanced} onChange={() => setShowAdvanced(!showAdvanced)} />}
                        label={t('advanced_perms')}
                      />
                    </FormGroup>
                  </Grid>

                  {showAdvanced ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <DropdownCheckbox
                          objects={permissions}
                          selectedObjects={selectedPermissions}
                          setSelectedObjects={setSelectedPermissions}
                          title="permissions"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DropdownCheckbox
                          objects={formRoles}
                          selectedObjects={selectedRoles}
                          setSelectedObjects={setSelectedRoles}
                          title="roles"
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <FormControl>
                        <FormLabel id="demo-row-radio-buttons-group-label">{t('role')}</FormLabel>
                        <RadioGroup
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                          value={selectedRadioRole}
                          onChange={handleRoleRadio}
                        >
                          {roleRadioControls}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Grid>
            <Grid container item xs={12} sm={12} spacing={2} direction="row">
              <Grid item>
                <Box>
                  <Button onClick={() => handleCloseModal()} variant="outlined">
                    {t('cancel')}
                  </Button>
                </Box>
              </Grid>
              <Grid item>
                <Box>
                  <Button type="submit" variant="outlined" color="primary">
                    {t('save')}
                  </Button>
                </Box>
              </Grid>
              {userToEdit &&
                userToEdit?.profile?.email != undefined &&
                userToEdit?.profile?.email != '' &&
                !userToEdit?.profile?.registration_complete && (
                  <>
                    <Grid item>
                      <Box>
                        <Button onClick={() => resendInvite()} variant="outlined" color="primary">
                          {t('resend_invite')}
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item>
                      <Box className={classes.resendInviteMessageBox}>
                        <Typography style={{ color: colorForResendInviteMessage }}>
                          {resendUserInviteMessage}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
            </Grid>
          </form>
        </Box>
      ) : (
        <div>
          <Typography component="h2" gutterBottom variant={matchesSmall ? 'h3' : 'h2'}>
            {t('user_confirm') + (userToEdit ? userToEdit.login : currentUser.login)}
          </Typography>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12}>
              {!userToEdit && <Typography>{t('login') + ': ' + formData.login}</Typography>}
              <Typography>{t('full_name') + ': ' + formData.display_name}</Typography>
              {userToEdit && formData.password !== '' && (
                <Typography className={classes.warning}>{t('password_been_reset')}</Typography>
              )}
              {!userToEdit && _.includes(currentUserRoles, Roles.Super) && (
                <Typography>{t('organization') + ': ' + formData.organization?.name}</Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Button style={{ marginRight: 24 }} onClick={() => setFormControl('form')}>
                {t('go_back')}
              </Button>
              <Button onClick={submitUser} variant="outlined" color="primary">
                {t('confirm')}
              </Button>
            </Grid>
          </Grid>
        </div>
      )}
    </Card>
  );
};

UserForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  instancePermissions: PropTypes.object,
  instanceRoles: PropTypes.object,
  userToEdit: PropTypes.object,
  organizations: PropTypes.array,
  selectedOrganization: PropTypes.object,
  currentUserRoles: PropTypes.array,
};

UserForm.defaultProps = {
  instancePermissions: undefined,
  instanceRoles: undefined,
  userToEdit: undefined,
  organizations: [],
  selectedOrganization: undefined,
  currentUserRoles: [],
};

export default UserForm;
